<template>
    <div class="row">
        <div class="col-12 mb-1">
            <div class="d-flex justify-content-between mb-25 font-small-3">
                <span class="font-weight-bold text-muted">Doluluk</span>
                <span :class="percentageClass" class="font-weight-bolder">{{ occupancyRate }}%</span>
            </div>

            <div class="progress-wrapper">
                <div class="progress-bg"></div>
                <div class="progress-fill" :class="barColorClass" :style="{ width: barWidth + '%' }"></div>
                <div class="capacity-marker" v-b-tooltip.hover title="Maksimum Kapasite"></div>
            </div>

            <div class="d-flex justify-content-between mt-25 font-small-1 text-muted">
                <span>0 lt</span>
                <span>Kapasite: {{ formatNumber(fuelStorage.Capacity) }} lt</span>
            </div>
        </div>

        <div class="col-12 mb-1">
            <div class="row">
                <div class="col-6 pr-50">
                    <div class="border rounded p-50 text-center bg-light">
                        <span class="d-block font-small-2 text-muted mb-25">Sistemdeki Stok</span>
                        <h5 class="font-weight-bolder mb-0">{{ formatNumber(fuelStorage.FuelQuantity) }}</h5>
                    </div>
                </div>
                <div class="col-6 pl-50">
                    <div class="border rounded p-50 text-center border-primary" style="border-style: dashed !important; background-color: #f8f8ff">
                        <span class="d-block font-small-2 text-primary mb-25">Yeni Bakiye</span>
                        <h5 class="font-weight-bolder text-primary mb-0">{{ value.NewBalance ? formatNumber(value.NewBalance) : "-" }}</h5>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-12">
            <label class="form-label font-small-3">Fiziki / Ölçülen Stok [lt] <span class="text-danger">*</span></label>
            <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                    <v-validator :errors="errors" parameter="NewBalance" :watch="value.NewBalance">
                        <VCurrencyInput ref="balanceInput" :value="value.NewBalance" @update-value="updateBalance" :class="{ 'is-invalid': errors.NewBalance, 'border-danger': isOverCapacity }" />
                    </v-validator>
                </div>
                <div class="ml-1" v-if="value.NewBalance !== null">
                    <b-badge :variant="differenceVariant" class="px-1">
                        <feather-icon :icon="differenceIcon" class="mr-25" />
                        {{ formatDifference(value.NewBalance - fuelStorage.FuelQuantity) }}
                    </b-badge>
                </div>
            </div>

            <div v-if="isOverCapacity" class="text-danger mt-50 font-small-2">
                <feather-icon icon="AlertTriangleIcon" size="14" class="mr-25" />
                Dikkat: Kapasite aşılıyor!
            </div>
        </div>
    </div>
</template>

<script>
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import { BBadge, VBTooltip } from "bootstrap-vue";

export default {
    name: "FuelStorageStockBalance",
    components: { VCurrencyInput, BBadge },
    directives: { "b-tooltip": VBTooltip },
    props: {
        value: { type: Object, required: true },
        fuelStorage: { type: Object, default: () => ({ Capacity: 0, FuelQuantity: 0 }) },
        errors: { type: Object, default: () => ({}) },
    },
    computed: {
        currentVal() {
            return this.value.NewBalance !== null ? this.value.NewBalance : this.fuelStorage.FuelQuantity;
        },
        occupancyRate() {
            if (!this.fuelStorage.Capacity || this.fuelStorage.Capacity <= 0) return 0;
            const rate = (this.currentVal / this.fuelStorage.Capacity) * 100;
            return rate.toFixed(1);
        },
        barWidth() {
            return Math.min(this.occupancyRate, 100);
        },
        isOverCapacity() {
            return this.currentVal > this.fuelStorage.Capacity;
        },
        barColorClass() {
            if (this.isOverCapacity) return "bg-danger-gradient";
            if (this.occupancyRate > 90) return "bg-warning-gradient";
            return "bg-primary-gradient";
        },
        percentageClass() {
            if (this.isOverCapacity) return "text-danger";
            if (this.occupancyRate > 90) return "text-warning";
            return "text-primary";
        },
        differenceVariant() {
            const diff = this.value.NewBalance - this.fuelStorage.FuelQuantity;
            if (diff > 0) return "light-success";
            if (diff < 0) return "light-danger";
            return "light-secondary";
        },
        differenceIcon() {
            const diff = this.value.NewBalance - this.fuelStorage.FuelQuantity;
            if (diff > 0) return "ArrowUpIcon";
            if (diff < 0) return "ArrowDownIcon";
            return "MinusIcon";
        },
    },
    methods: {
        updateBalance(val) {
            let finalVal = val;

            if (val === null && this.$refs.balanceInput) {
                const rawValue = this.$refs.balanceInput.$el.value;
                if (rawValue && parseFloat(rawValue.replace(",", ".")) === 0) {
                    finalVal = 0;
                }
            }

            this.$emit("input", { ...this.value, NewBalance: finalVal });
        },
        formatNumber(val) {
            if (val === null || val === undefined) return "0";
            return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
        },
        formatDifference(val) {
            return (val > 0 ? "+" : "") + new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(val);
        },
    },
};
</script>

<style scoped>
.progress-wrapper {
    position: relative;
    height: 1rem;
    background-color: #f0f2f5;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.progress-fill {
    height: 100%;
    border-radius: 0.5rem;
    transition: width 0.4s ease;
    position: relative;
    overflow: hidden;
}

.bg-primary-gradient {
    background: linear-gradient(90deg, #7367f0, #9e95f5);
}
.bg-warning-gradient {
    background: linear-gradient(90deg, #ff9f43, #ffc085);
}
.bg-danger-gradient {
    background: linear-gradient(90deg, #ea5455, #f08182);
}

.capacity-marker {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #ea5455;
    z-index: 5;
}
</style>
